import { createClientRegistrationServiceImpl } from './clientRegistrationService/internal/ClientRegistrationServiceImpl';
import { PlatformAuthorizer, PlatformClient, AuthorizationUtil } from '@quickplay/platform-authorizer';
import { ClientRegistrationService } from '../ClientRegistrationService';
import {
  createKeyValueStore,
  LocalStorage,
  Error,
  isError,
  JWTHeader,
  JWTUtil,
  Algorithm,
  JWTPayload,
  CryptoJS,
  HttpHeaders,
  AsyncUtil,
  HttpRequest,
  createRequestBuilder,
} from '@quickplay/foundation';
import { ClientAuthorizationToken } from './clientRegistrationService/model/ClientAuthorizationToken';
import { ContentAuthorizationData } from './playbackAuthorizationService/model/ContentAuthorizationData';
import { PlaybackAuthorizationService } from '../PlaybackAuthorizationService';
import { RenewLicenseService } from '../RenewLicenseService';
import { ContentAuthorizer } from '../ContentAuthorizer';
import { playbackAuthorizationServiceImpl } from './playbackAuthorizationService/internal/PlaybackAuthorizationServiceImpl';
import { PlatformAsset } from '../PlatformAsset';
import { ContentAuthorizationConfiguration } from '../ContentAuthorizationConfiguration';
import {
  DeliveryType,
  PlaybackAuthorizationRequestData,
} from './playbackAuthorizationService/internal/PlaybackAuthorizationRequest';
import { createRenewLicenseServiceImpl } from './renewLicenseService/internal/RenewLicenseServiceImpl';
import { RenewLicenseAsset } from '../RenewLicenseAsset';
import { CheckRedeemService } from '../CheckRedeemService';
import { createCheckRedeemServiceImpl } from './checkRedeemService/internal/CheckRedeemServiceImpl';
import { CheckRedeemAsset } from '../CheckRedeemAsset';
import { CheckRedeemRequest } from './checkRedeemService/model/CheckRedeemRequest';
import { PartnerAuthorizationData } from './partnerAuthorization/PartnerAuthorizationData';
import { PartnerAuthorizationRequestData } from './partnerAuthorization/PartnerAuthorizationRequestData';

const CONTENT_AUTHORIZER_STORE_NAME = `CONTENT_AUTHORIZER_STORE_NAME`;
const CONTENT_AUTHORIZER_STORE_ACCESS_TOKEN = `CONTENT_AUTHORIZER_STORE_ACCESS_TOKEN`;
const JWT_DEFAULT_AUDIENCE = 'playback-auth-service';
const X_DEVICE_ID_HEADER = 'X-Device-Id';
const CONTENT_TYPE_HEADER = 'Content-Type';
const CONTENT_TYPE_VALUE = 'application/json';

type PlaybackPartnerAuthorizationRequestData = Pick<
  PlaybackAuthorizationRequestData,
  'deviceName' | 'deviceId' | 'contentId' | 'contentTypeId' | 'catalogType'
>;
export type PartnerPlatformAsset = Pick<PlatformAsset, 'mediaID' | 'consumptionType' | 'catalogType'>;

export function createContentAuthorizer(
  contentAuthorizationConfiguration: ContentAuthorizationConfiguration,
  platform: PlatformAuthorizer,
  platformClient: PlatformClient,
  platformProxyClient?: PlatformClient,
): ContentAuthorizer {
  const maxRetries = contentAuthorizationConfiguration.maxRetries;
  const internalKeyValueStore = createKeyValueStore<ClientAuthorizationToken>(
    CONTENT_AUTHORIZER_STORE_NAME,
    sessionStorage as LocalStorage,
  );
  const clientRegistrationService: ClientRegistrationService = createClientRegistrationServiceImpl(
    contentAuthorizationConfiguration.clientRegistrationEndPointUrl,
    platform,
  );

  let renewLicenseService: RenewLicenseService | undefined = undefined;
  let checkRedeemService: CheckRedeemService | undefined = undefined;

  const playbackAuthorizationService: PlaybackAuthorizationService = playbackAuthorizationServiceImpl(
    contentAuthorizationConfiguration.endPointUrl,
    platform,
  );

  const contentAuthorizer: ContentAuthorizer = {
    async registerDevice(): Promise<void> {
      return AsyncUtil.retryOnNetworkFailure(() => ensureDeviceRegistration(), maxRetries);
    },

    async renewLicense(renewLicenseAsset: RenewLicenseAsset): Promise<string> {
      return renewLicenseUrl(renewLicenseAsset);
    },

    async authorizePlayback(platformAsset: PlatformAsset, headers?: HttpHeaders): Promise<ContentAuthorizationData> {
      return AsyncUtil.retryOnNetworkFailure(() => authorizeContent(platformAsset, headers), maxRetries);
    },

    invalidateDeviceToken(): void {
      internalKeyValueStore.clear();
    },

    async checkRedeem(checkRedeemAsset: CheckRedeemAsset, headers?: HttpHeaders): Promise<string> {
      return AsyncUtil.retryOnNetworkFailure(() => checkRedeem(checkRedeemAsset, headers), maxRetries);
    },
    authorizePartnerPlayback: function (
      platformAsset: PartnerPlatformAsset,
      headers?: HttpHeaders,
    ): Promise<PartnerAuthorizationData> {
      return AsyncUtil.retryOnNetworkFailure(() => authorizePartnerPlayback(platformAsset, headers), maxRetries);
    },
  };

  function getClientRegistrationToken(): ClientAuthorizationToken | undefined {
    return internalKeyValueStore.get(CONTENT_AUTHORIZER_STORE_ACCESS_TOKEN) as ClientAuthorizationToken;
  }

  async function ensureDeviceRegistration(): Promise<void> {
    try {
      const clientRegistrationToken = getClientRegistrationToken();
      if (clientRegistrationToken) {
        return Promise.resolve();
      }
      const deviceAuthorizationToken: ClientAuthorizationToken | Error = await clientRegistrationService.registerDevice(
        platformClient.id,
        platformProxyClient && platformProxyClient.id,
      );

      if (isError(deviceAuthorizationToken)) {
        return Promise.reject(deviceAuthorizationToken as Error);
      }

      internalKeyValueStore.set(CONTENT_AUTHORIZER_STORE_ACCESS_TOKEN, deviceAuthorizationToken);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function generateDeviceToken(clientAuthorizationToken: ClientAuthorizationToken): string {
    const platformServiceTime = AuthorizationUtil.platformServiceTime();
    const algo = Algorithm.HS256;
    const now = platformServiceTime > 0 ? platformServiceTime / 1000 : new Date().getTime() / 1000;
    const secret = CryptoJS.enc.Base64.parse(clientAuthorizationToken.secret);
    const header: JWTHeader = { alg: algo, typ: 'JWT' };
    interface Payload extends JWTPayload {
      deviceId: string;
    }
    const payload: Payload = {
      deviceId: clientAuthorizationToken.deviceId,
      aud: JWT_DEFAULT_AUDIENCE,
      iat: now,
      exp: now + clientAuthorizationToken.expiry,
    };

    return JWTUtil.encode(header, payload, secret, algo);
  }

  async function checkRedeem(checkRedeemAsset: CheckRedeemAsset, headers?: HttpHeaders): Promise<string> {
    if (!contentAuthorizationConfiguration.checkRedeemEndPointUrl) {
      return Promise.reject();
    }

    try {
      await ensureDeviceRegistration();
      const clientAuthorizationToken = getClientRegistrationToken();
      if (!clientAuthorizationToken) {
        return Promise.reject();
      }

      const deviceToken = generateDeviceToken(clientAuthorizationToken);

      if (!deviceToken) {
        return Promise.reject(deviceToken);
      }

      if (!checkRedeemService) {
        checkRedeemService = createCheckRedeemServiceImpl(
          contentAuthorizationConfiguration.checkRedeemEndPointUrl,
          platform,
        );
      }

      const checkRedeemRequestData: CheckRedeemRequest = {
        deviceId: clientAuthorizationToken.deviceId,
        contentId: checkRedeemAsset.mediaID,
        contentTypeId: checkRedeemAsset.consumptionType,
        catalogType: checkRedeemAsset.catalogType,
        delivery: checkRedeemAsset.delivery,
        deviceToken: deviceToken,
      };

      const token: string | Error = await checkRedeemService.checkRedeem(checkRedeemRequestData, headers);

      if (isError(token)) {
        return Promise.reject(token);
      }

      return Promise.resolve(token);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function renewLicenseUrl(renewLicenseAsset: RenewLicenseAsset): Promise<string> {
    if (!contentAuthorizationConfiguration.renewLicenseEndPointUrl) {
      return Promise.reject();
    }

    const clientAuthorizationToken = getClientRegistrationToken();
    if (!clientAuthorizationToken) {
      return Promise.reject();
    }

    if (!renewLicenseService) {
      renewLicenseService = createRenewLicenseServiceImpl(
        contentAuthorizationConfiguration.renewLicenseEndPointUrl,
        platform,
      );
    }

    const deviceToken = generateDeviceToken(clientAuthorizationToken);

    try {
      const licenseUrl = await renewLicenseService?.renewLicense(
        {
          deviceId: clientAuthorizationToken.deviceId,
          deviceName: platformClient.name,
          contentId: renewLicenseAsset.mediaID,
          drm: renewLicenseAsset.drmScheme,
          delivery: DeliveryType.STREAMING,
          licenseUrl: renewLicenseAsset.licenseUrl,
          licenseRefreshToken: renewLicenseAsset.licenseRefreshToken,
          castDeviceId: platformProxyClient?.id,
        },
        { [X_DEVICE_ID_HEADER]: deviceToken },
      );

      if (!licenseUrl || isError(licenseUrl)) {
        return Promise.reject(licenseUrl);
      }

      return Promise.resolve(licenseUrl);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function buildPlaybackAuthorizationRequestData(
    platformClient_: PlatformClient,
    clientAuthorizationToken: ClientAuthorizationToken,
    platformAsset: PlatformAsset,
    deviceToken: string,
    platformProxyClient_?: PlatformClient,
  ): PlaybackAuthorizationRequestData {
    return {
      deviceName: platformClient_.name,
      deviceId: clientAuthorizationToken.deviceId,
      contentId: platformAsset.mediaID,
      contentTypeId: platformAsset.consumptionType,
      catalogType: platformAsset.catalogType,
      mediaFormat: platformAsset.mediaType,
      drm: platformAsset.drmScheme,
      delivery: DeliveryType.STREAMING,
      disableSsai: platformAsset.disableSsai ? 'true' : 'false',
      disableCsai: platformAsset.disableCsai ? 'true' : 'false',
      startTime: platformAsset.startTime,
      endTime: platformAsset.endTime,
      playbackMode: platformAsset.playbackMode,
      eventId: platformAsset.eventId,
      castDeviceId: platformProxyClient_ && platformProxyClient_.id,
      deviceManufacturer: platformAsset.deviceManufacturer ?? platformClient_.deviceManufacturer,
      deviceModelName: platformAsset.deviceModelName ?? platformClient_.deviceModelName,
      deviceModelNumber: platformAsset.deviceModelNumber ?? platformClient_.deviceModelNumber,
      deviceYear: platformAsset.deviceYear ?? platformClient_.deviceYear,
      deviceOs: platformAsset.deviceOs ?? platformClient_.deviceOs,
      deviceOsVersion: platformAsset.deviceOsVersion ?? platformClient_.deviceOsVersion,
      supportedResolution: platformAsset.supportedResolution ?? platformClient_.supportedResolution,
      supportedAudio: platformAsset.supportedAudio ?? platformClient_.supportedAudio,
      supportedAudioCodecs: platformAsset.supportedAudioCodecs ?? platformClient_.supportedAudioCodecs,
      supportedVideoCodecs: platformAsset.supportedVideoCodecs ?? platformClient_.supportedVideoCodecs,
      supportedMaxWVSecurityLevel:
        platformAsset.supportedMaxWVSecurityLevel ?? platformClient_.supportedMaxWVSecurityLevel,
      supportedHdrCapabilities: platformAsset.supportedHdrCapabilities ?? platformClient_.supportedHdrCapabilities,
      deviceToken: deviceToken,
      urlParameters: platformAsset.urlParameters,
      prid: platformAsset.primaryContentId,
      custom: platformAsset.custom,
    };
  }

  async function authorizeContent(
    platformAsset: PlatformAsset,
    headers?: HttpHeaders,
  ): Promise<ContentAuthorizationData> {
    try {
      await ensureDeviceRegistration();
      const clientAuthorizationToken = getClientRegistrationToken();
      if (clientAuthorizationToken) {
        const deviceToken = generateDeviceToken(clientAuthorizationToken);

        if (!deviceToken) {
          return Promise.reject(deviceToken);
        }

        const playbackAuthData: PlaybackAuthorizationRequestData = buildPlaybackAuthorizationRequestData(
          platformClient,
          clientAuthorizationToken,
          platformAsset,
          deviceToken,
          platformProxyClient,
        );

        const contentAuthorizationData: ContentAuthorizationData | Error =
          await playbackAuthorizationService.authorizePlayback(playbackAuthData, headers);
        if (isError(contentAuthorizationData)) {
          return Promise.reject(contentAuthorizationData);
        }
        return Promise.resolve(contentAuthorizationData);
      }
      return Promise.reject();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  //PartnerAuthorizerContent
  function buildPartnerAuthorizeRequest(
    requestData: PlaybackPartnerAuthorizationRequestData,
    deviceToken: string,
    headers?: HttpHeaders,
  ): HttpRequest<PlaybackPartnerAuthorizationRequestData> | undefined {
    if (!contentAuthorizationConfiguration.syndicationSSOEndPointUrl) {
      return undefined;
    }

    const defaultHeader: HttpHeaders = { [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE, [X_DEVICE_ID_HEADER]: deviceToken };
    return createRequestBuilder<PlaybackPartnerAuthorizationRequestData>()
      .url(contentAuthorizationConfiguration.syndicationSSOEndPointUrl)
      .headers(Object.assign(defaultHeader, headers))
      .path('/partner/content/authorize')
      .post(requestData)
      .build();
  }

  async function buildAuthorizePartnerPlayback(
    requestData: PartnerAuthorizationRequestData,
    deviceToken: string,
    headers?: HttpHeaders,
  ): Promise<PartnerAuthorizationData> {
    try {
      const request = buildPartnerAuthorizeRequest(requestData, deviceToken, headers);
      if (!request) {
        return Promise.reject();
      }

      const result = await AuthorizationUtil.makeAuthorizedRequest<any, PartnerAuthorizationData>(request, platform);

      return {
        contentId: result.data.contentId,
        deeplinkUrl: result.data.deeplinkUrl,
        deeplinkToken: result.data.deeplinkToken,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function buildPartnerAuthorizeRequestData(
    platformClient_: PlatformClient,
    clientAuthorizationToken: ClientAuthorizationToken,
    platformAsset: PartnerPlatformAsset,
  ): PartnerAuthorizationRequestData {
    return {
      deviceName: platformClient_.name,
      deviceId: clientAuthorizationToken.deviceId,
      contentId: platformAsset.mediaID,
      contentTypeId: platformAsset.consumptionType,
      catalogType: platformAsset.catalogType,
    };
  }

  async function authorizePartnerPlayback(
    platformAsset: PartnerPlatformAsset,
    headers?: HttpHeaders,
  ): Promise<PartnerAuthorizationData> {
    try {
      await ensureDeviceRegistration();
      const clientAuthorizationToken = getClientRegistrationToken();
      if (!clientAuthorizationToken) {
        return Promise.reject(clientAuthorizationToken);
      }
      const deviceToken = generateDeviceToken(clientAuthorizationToken);
      if (!deviceToken) {
        return Promise.reject(deviceToken);
      }

      const requestData = buildPartnerAuthorizeRequestData(platformClient, clientAuthorizationToken, platformAsset);
      const partnerAuthorizationData: PartnerAuthorizationData | Error = await buildAuthorizePartnerPlayback(
        requestData,
        deviceToken,
        headers,
      );
      if (isError(partnerAuthorizationData)) {
        return Promise.reject(partnerAuthorizationData);
      }
      return Promise.resolve(partnerAuthorizationData);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  return contentAuthorizer;
}
